import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, Building2, RefreshCw } from 'lucide-react';
import { matchingAPI } from '../../lib/api';

function getErrorMessage(err) {
  return err?.response?.data?.detail || err?.message || 'Request failed';
}

// Scoped read of the same GET /matches/match-funnel endpoint the BR OS and
// accounting evidence cards use (routes/matching.py) -- whether the chain
// is discovering portfolio-scheduler evidence at all, and whether armed
// auto-confirm attempts are succeeding or being refused downstream.
const FUNNEL_TALLY_KEYS = [
  { key: 'portfolio_evidence_stamped', label: 'Evidence stamped' },
  { key: 'portfolio_auto_confirm_queued', label: 'Queued for confirm' },
  { key: 'portfolio_auto_confirmed', label: 'Auto-confirmed' },
  { key: 'portfolio_auto_confirm_failed', label: 'Auto-confirm failed' },
];

export function PortfolioReconciledEvidenceTierPanel() {
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [funnelNotFound, setFunnelNotFound] = useState(false);
  const [funnelError, setFunnelError] = useState(null);

  const runPreview = async () => {
    setPreviewLoading(true);
    setApplyResult(null);
    try {
      const res = await matchingAPI.previewPortfolioReconciledEvidenceTier();
      setPreview(res.data);
    } catch (err) {
      setPreview({ error: getErrorMessage(err) });
    } finally {
      setPreviewLoading(false);
    }
  };

  const runFunnel = async () => {
    setFunnelLoading(true);
    setFunnelError(null);
    setFunnelNotFound(false);
    try {
      const { data } = await matchingAPI.matchFunnel();
      setFunnel(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setFunnelNotFound(true);
        setFunnel(null);
      } else {
        setFunnelError(getErrorMessage(err));
      }
    } finally {
      setFunnelLoading(false);
    }
  };

  const runApply = async () => {
    if (!window.confirm(
      `Arm auto-confirm for ${preview?.would_auto_confirm_count || 0} portfolio-scheduler-reconciled, ` +
      'pinned match(es)? This is a one-time switch -- confirms then happen automatically on the ' +
      'next scheduled auto-matching run, through the normal confirm path. Not reversible from ' +
      'here (revert individual matches via Reconciliation if needed).'
    )) return;
    setApplyLoading(true);
    try {
      const res = await matchingAPI.applyPortfolioReconciledEvidenceTier();
      setApplyResult(res.data);
    } catch (err) {
      setApplyResult({ error: getErrorMessage(err) });
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Portfolio-Reconciled Evidence Tier</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Uses the portfolio payment-marking scheduler's own applies (Pennylane channel invoice
          payouts it has already marked paid against a specific bank-feed transaction) as match
          evidence: a uniquely-pinned pairing can auto-confirm once armed, bridging the
          already-portfolio-paid-but-still-unreconciled-in-Entities gap. Every record here
          self-originates (the scheduler never touches db.matches directly). Preview shows what
          would auto-confirm; Apply backfills any pre-existing pairings and is a one-time arm,
          not a repeated action.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runPreview} disabled={previewLoading} data-testid="portfolio-evidence-tier-preview">
            {previewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && !preview.error && !preview.armed && (
            <Button
              variant="default"
              size="sm"
              onClick={runApply}
              disabled={applyLoading || !!applyResult}
              data-testid="portfolio-evidence-tier-apply"
            >
              {applyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Arm Auto-Confirm ({preview.would_auto_confirm_count || 0})
            </Button>
          )}
        </div>

        {preview && (
          <>
            {preview.error ? (
              <p className="text-xs text-red-600">{preview.error}</p>
            ) : (
              <div className="space-y-2" data-testid="portfolio-evidence-tier-preview-result">
                <div className="flex flex-wrap gap-2">
                  {preview.armed && (
                    <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px]" data-testid="portfolio-evidence-tier-armed-badge">
                      Armed
                    </Badge>
                  )}
                  {preview.tallies && Object.entries(preview.tallies).map(([key, count]) => (
                    <Badge key={key} variant="outline" className="text-[10px]" data-testid={'portfolio-evidence-tally-' + key}>
                      {key}: {count}
                    </Badge>
                  ))}
                  {preview.truncated && (
                    <span className="text-amber-600 text-xs">Truncated at scan cap</span>
                  )}
                </div>
                {preview.would_auto_confirm && preview.would_auto_confirm.length > 0 && (
                  <ul className="text-xs space-y-1" data-testid="portfolio-evidence-would-auto-confirm-list">
                    {preview.would_auto_confirm.map((w) => (
                      <li key={w.match_id || `${w.transaction_id}:${w.invoice_id}`} className="font-mono">
                        {w.invoice_id} · {w.transaction_id} · {w.currency || ''} {w.amount ?? ''} · {w.entity_id || 'unresolved entity'}
                        {w.self_minted && (
                          <Badge variant="outline" className="ml-1 text-[9px] align-middle">self-originated</Badge>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {preview.amount_divergent_pin && preview.amount_divergent_pin.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                      Amount-divergent pins (reserved for partial payments, never auto-confirm):
                    </p>
                    <ul className="text-xs space-y-1" data-testid="portfolio-evidence-amount-divergent-list">
                      {preview.amount_divergent_pin.map((d) => (
                        <li key={`${d.transaction_id}:${d.invoice_id}`} className="font-mono text-amber-700 dark:text-amber-400">
                          {d.transaction_id} · {d.invoice_id} · {d.source_system}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {preview.blocked && preview.blocked.length > 0 && (
                  <ul className="text-xs space-y-1" data-testid="portfolio-evidence-blocked-list">
                    {preview.blocked.map((b) => (
                      <li key={b.match_id} className="font-mono text-amber-700 dark:text-amber-400">
                        {b.transaction_id} · {b.invoice_id} · blocked: {b.reason}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs text-muted-foreground">{preview.message}</p>
              </div>
            )}
          </>
        )}

        {applyResult && (
          <div className={`p-3 rounded text-xs border ${applyResult.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`} data-testid="portfolio-evidence-tier-apply-result">
            {applyResult.error ? (
              <p className="text-red-600">{applyResult.error}</p>
            ) : (
              <div className="space-y-1">
                <p><strong>Armed:</strong> {applyResult.newly_armed ? 'yes (just now)' : 'already armed'}</p>
                <p className="text-muted-foreground">{applyResult.message}</p>
              </div>
            )}
          </div>
        )}

        <div className="pt-2 border-t space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Auto-match chain funnel tallies for this tier (from the latest chain run) --
              whether evidence is being discovered and whether armed auto-confirms are
              succeeding, distinct from the Preview projection above.
            </p>
            <Button variant="outline" size="sm" onClick={runFunnel} disabled={funnelLoading} data-testid="portfolio-evidence-funnel-refresh">
              {funnelLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
              Refresh
            </Button>
          </div>

          {funnelError && <p className="text-xs text-red-600">{funnelError}</p>}

          {funnelNotFound && (
            <p className="text-xs text-muted-foreground" data-testid="portfolio-evidence-funnel-empty">
              No auto-match chain yet for this scope -- run auto-matching from the Reconciliation page first.
            </p>
          )}

          {funnel && (
            <div className="flex flex-wrap gap-2" data-testid="portfolio-evidence-funnel-result">
              {FUNNEL_TALLY_KEYS.map(({ key, label }) => (
                <Badge key={key} variant="outline" className="text-[10px]" data-testid={'portfolio-evidence-funnel-tally-' + key}>
                  {label}: {(funnel.stats || {})[key] ?? 0}
                </Badge>
              ))}
            </div>
          )}

          {funnel && (funnel.stats?.samples?.portfolio_auto_confirm_failures || []).length > 0 && (
            <div className="pt-1 space-y-1" data-testid="portfolio-evidence-funnel-failure-samples">
              <p className="text-xs text-muted-foreground">Auto-confirm failure reasons (most recent sample):</p>
              <ul className="text-xs space-y-1">
                {funnel.stats.samples.portfolio_auto_confirm_failures.map((f, i) => (
                  <li key={f.ref_id || i} className="font-mono text-red-700 dark:text-red-400">
                    {f.kind}:{f.ref_id} · {f.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
